import React, { useState } from "react"
import Popup from "reactjs-popup"
import { FaComments } from "react-icons/fa"
import { useAuth0 } from "@auth0/auth0-react"
import useLogging from "../../hooks/useLogging"

const WeeklyCNForm = ({ isOpen, onClose, onSubmit }) => {
	const { user, getAccessTokenSilently } = useAuth0()
	const logging = useLogging()
	const [helpful, setHelpful] = useState(null)
	const [questions, setQuestions] = useState("")
	const [helpfulAspects, setHelpfulAspects] = useState("")
	const [improvementAspects, setImprovementAspects] = useState("")

	// Descriptions for scales

	const helpfulDescriptions = [
		"Not helpful",
		"",
		"",
		"",
		"",
		"",
		"",
		"",
		"",
		"",
		"Extremely helpful"
	]

	const handleSubmit = async close => {
		// Validate that the helpful rating is provided
		if (helpful === null) {
			alert("Please rate how helpful Oncare was today")
			return
		}

		// Preparing the feedback data with user information
		const feedbackData = {
			scores: {
				helpful: helpful
			},
			comments: {
				helpfulAspects: helpfulAspects || "", // Optional field
				improvementAspects: improvementAspects || "", // Optional field
				questions: questions || "" // Optional field
			},
			userId: user.sub,
			userName: user.name
		}

		try {
			const token = await getAccessTokenSilently()

			// Pass the feedback data to the parent component's onSubmit handler
			await onSubmit(feedbackData, token)

			// Reset all fields after successful submission
			setHelpful(null)
			setHelpfulAspects("")
			setImprovementAspects("")
			setQuestions("")

			// Close the popup explicitly using the close function from Popup
			close()
		} catch (error) {
			logging.warn("Error submitting feedback:", {
				error: error.message,
				stack: error.stack
			})
		}
	}

	return (
		<Popup
			trigger={
				<button className="fixed bottom-2 right-16 bg-gray-100 text-dark text-sm font-semibold px-4 py-3 m-2 rounded flex items-center space-x-2 z-[8]">
					<span role="img" aria-label="feedback-emoji">
						😄
					</span>
					<span>Provide Feedback</span>
				</button>
			}
			modal
			nested
			contentStyle={{
				width: "100%",
				maxWidth: "900px",
				maxHeight: "90vh",
				border: "none",
				borderRadius: "12px",
				padding: "0",
				overflow: "hidden",
				position: "relative"
			}}
			overlayStyle={{
				background: "rgba(0, 0, 0, 0.5)",
				overflow: "hidden"
			}}
			open={isOpen}
			onClose={onClose}
			lockScroll={true}
		>
			{close => (
				<div className="bg-white p-8 rounded-xl shadow-md w-full mx-auto max-h-[80vh] overflow-y-auto">
					<div className="flex items-center justify-between mb-6">
						<div className="flex items-center">
							<div className="bg-gray-200 rounded-full p-2">
								<FaComments aria-label="feedback-icon" className="text-lg" />
							</div>
							<div className="ml-3 text-lg font-semibold">Feedback</div>
						</div>
						<button
							onClick={close}
							className="text-gray-400 hover:text-gray-600 text-4xl"
						>
							×
						</button>
					</div>
					<hr className="mb-6 border-gray-200" />

					{/* Question 1 */}
					<div className="text-xl font-semibold mb-4 text-center">
						How helpful was Oncare this week? <span className="text-red-500">*</span>
					</div>
					<div className="flex flex-wrap justify-center items-center mb-2">
						{helpfulDescriptions.map((desc, index) => (
							<div key={index} className="text-center">
								<button
									className={`w-16 m-1 h-16 border rounded-lg flex items-center justify-center hover:bg-gray-100 ${
										helpful === index
											? "bg-gray-100 text-black font-bold border-blue-500 border-2 shadow-[0px_0px_4px_1px_#003bff94]"
											: "bg-gray-100 text-gray-500 border-gray-300 hover:scale-110 hover:font-semibold"
									}`}
									onClick={() => setHelpful(index)}
								>
									{index}
								</button>
							</div>
						))}
					</div>

					<div className="flex flex-wrap justify-center items-center mb-6">
						{helpfulDescriptions.map((desc, index) => (
							<div key={index} className="relative w-16 m-1 h-2">
								<div
									className="text-sm capitalize whitespace-nowrap absolute"
									style={{
										left: "50%",
										transform: "translateX(-50%)"
									}}
								>
									{desc}
								</div>
							</div>
						))}
					</div>

					{/* Question 2 */}
					<div className="text-xl font-semibold mb-4 text-center">
						What aspects of Oncare are helpful to you?
					</div>
					<textarea
						className="w-full border border-gray-300 bg-gray-100 rounded-lg p-4 text-sm focus:outline-none focus:ring-0 focus:border-gray-400 mb-6"
						placeholder="Tell us what features or aspects you find most helpful..."
						value={helpfulAspects}
						onChange={e => setHelpfulAspects(e.target.value)}
					/>

					{/* Question 3 */}
					<div className="text-xl font-semibold mb-4 text-center">
						What aspects need improvement to meet your needs?
					</div>
					<textarea
						className="w-full border border-gray-300 bg-gray-100 rounded-lg p-4 text-sm focus:outline-none focus:ring-0 focus:border-gray-400 mb-6"
						placeholder="Let us know what could be improved..."
						value={improvementAspects}
						onChange={e => setImprovementAspects(e.target.value)}
					/>

					{/* Question 4 - New Question */}
					<div className="text-xl font-semibold mb-4 text-center">
						Do you have any other open questions or suggestions for Friday's meeting?
					</div>
					<textarea
						className="w-full border border-gray-300 bg-gray-100 rounded-lg p-4 text-sm focus:outline-none focus:ring-0 focus:border-gray-400 mb-6"
						placeholder=" Questions about schedule book changes, Suggestions about CN workflow,
						Questions about functionality..."
						value={questions}
						onChange={e => setQuestions(e.target.value)}
					/>

					{/* Submit Button */}
					<button
						onClick={() => handleSubmit(close)}
						className="w-full bg-gradient-to-r from-green-400 to-blue-500 text-white text-lg font-semibold py-3 rounded-lg hover:from-green-500 hover:to-blue-600"
					>
						Submit Now
					</button>
				</div>
			)}
		</Popup>
	)
}

export default WeeklyCNForm
